/**
 *
 * @param component 需要注册的组件
 * @param alias 组件别名
 * @returns any
 */
export const withInstall = <T>(component: T, alias?: string) => {
  const comp = component as any
  comp.install = (app: any) => {
    app.component(comp.name || comp.displayName, component)
    if (alias) {
      app.config.globalProperties[alias] = component
    }
  }
  return component as T & Plugin
}

/**
 * @param str 需要转下划线的驼峰字符串
 * @returns 字符串下划线
 */
export const humpToUnderline = (str: string): string => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase()
}

/**
 * @param str 需要转驼峰的下划线字符串
 * @returns 字符串驼峰
 */
export const underlineToHump = (str: string): string => {
  if (!str) return ''
  return str.replace(/\-(\w)/g, (_, letter: string) => {
    return letter.toUpperCase()
  })
}

/**
 * 驼峰转横杠
 */
export const humpToDash = (str: string): string => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase()
}

export const setCssVar = (prop: string, val: any, dom = document.documentElement) => {
  dom.style.setProperty(prop, val)
}

export const getCssVar = (prop: string, dom = document.documentElement) => {
  return getComputedStyle(dom).getPropertyValue(prop)
}

/**
 * 查找数组对象的某个下标
 * @param {Array} ary 查找的数组
 * @param {Functon} fn 判断的方法
 */
// eslint-disable-next-line
export const findIndex = <T = Recordable>(ary: Array<T>, fn: Fn): number => {
  if (ary.findIndex) {
    return ary.findIndex(fn)
  }
  let index = -1
  ary.some((item: T, i: number, ary: Array<T>) => {
    const ret: T = fn(item, i, ary)
    if (ret) {
      index = i
      return ret
    }
  })
  return index
}

export const trim = (str: string) => {
  return str.replace(/(^\s*)|(\s*$)/g, '')
}

/**
 * @param {Date | number | string} time 需要转换的时间
 * @param {String} fmt 需要转换的格式 如 yyyy-MM-dd、yyyy-MM-dd HH:mm:ss
 */
export function formatTime(time: Date | number | string, fmt: string) {
  if (!time) return ''
  else {
    const date = new Date(time)
    const o = {
      'M+': date.getMonth() + 1,
      'd+': date.getDate(),
      'H+': date.getHours(),
      'm+': date.getMinutes(),
      's+': date.getSeconds(),
      'q+': Math.floor((date.getMonth() + 3) / 3),
      S: date.getMilliseconds()
    }
    if (/(y+)/.test(fmt)) {
      fmt = fmt.replace(RegExp.$1, (date.getFullYear() + '').substr(4 - RegExp.$1.length))
    }
    for (const k in o) {
      if (new RegExp('(' + k + ')').test(fmt)) {
        fmt = fmt.replace(
          RegExp.$1,
          RegExp.$1.length === 1 ? o[k] : ('00' + o[k]).substr(('' + o[k]).length)
        )
      }
    }
    return fmt
  }
}

/**
 * 生成随机字符串
 */
export function toAnyString() {
  const str: string = 'xxxxx-xxxxx-4xxxx-yxxxx-xxxxx'.replace(/[xy]/g, (c: string) => {
    const r: number = (Math.random() * 16) | 0
    const v: number = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString()
  })
  return str
}

/**
 * 首字母大写
 */
export function firstUpperCase(str: string) {
  return str.toLowerCase().replace(/( |^)[a-z]/g, (L) => L.toUpperCase())
}

/**
 * 把对象转为formData，支持嵌套对象和数组
 */
export function objToFormData(obj: Recordable) {
  const formData = new FormData()

  const appendFormData = (key: string, value: any) => {
    // 1. 处理 null 或 undefined，忽略不追加
    if (value === null || value === undefined) {
      return
    }

    // 2. 处理 File 和 Blob，直接追加
    if (value instanceof File || value instanceof Blob) {
      formData.append(key, value)
      return
    }

    // 3. 处理数组，递归展平
    if (Array.isArray(value)) {
      value.forEach((item) => {
        appendFormData(key, item)
      })
      return
    }

    // 4. 处理普通对象，递归展平为 key[subKey] 格式
    if (typeof value === 'object') {
      Object.keys(value).forEach((subKey) => {
        appendFormData(`${key}[${subKey}]`, value[subKey])
      })
      return
    }

    // 5. 处理基本类型，转为字符串追加
    formData.append(key, String(value))
  }

  Object.keys(obj).forEach((key) => {
    appendFormData(key, obj[key])
  })

  return formData
}

/**
 * @param { string | number } value 格式化input只能输入正整数
 */
export const formatterInputPositiveInteger = (value: string | number) => {
  return `${value}`.replace(/[^1-9]/g, '')
}
/**
 * @param { string | number } value 格式化input只能输入整数
 */
export const formatterInputInteger = (value: string | number) => {
  return `${value}`.replace(/[^0-9]/g, '')
}

/**
 * @param { string | number } value 格式化input只能输入>0的数字
 */
export const formatterInputGreaterZero = (value: string | number) => {
  // 判断value第一个字符是否为0
  if (`${value}`.charAt(0) === '0') {
    // 如果为0就删除
    return `${value}`.replace(/^0/, '1')
  }
  return `${value}`.replace(/[^0-9]/g, '')
}

/**
 * Base64字符串转二进制流
 * @param {String} dataurl Base64字符串(字符串包含Data URI scheme，例如：data:image/png;base64, )
 */
export const dataURLtoBlob = (dataurl: string) => {
  const arr = dataurl.split(',')
  const mime = (arr[0].match(/:(.*?);/) as RegExpMatchArray)[1]
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new Blob([u8arr], {
    type: mime
  })
}

/**
 * Base64字符串转File文件
 * @param {String} dataurl Base64字符串(字符串包含Data URI scheme，例如：data:image/png;base64, )
 * @param {String} filename 文件名称
 */
export const dataURLtoFile = (dataurl: string, filename: string) => {
  const arr = dataurl.split(',')
  const mime = (arr[0].match(/:(.*?);/) as RegExpMatchArray)[1]
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, {
    type: mime
  })
}

/**
 * 定义义一个函数readFile，用于读取文件内容并将其转换为二进制字符串，同时处理可能的错误。
 * @param {File} file 文件对象
 */
export const readFile = (file: File) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsBinaryString(file)
    reader.onload = () => {
      resolve(reader.result)
    }
    reader.onerror = (error) => {
      reject(error)
    }
  })
}

export const generateUUID = () => {
  if (typeof crypto === 'object') {
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
    if (typeof crypto.getRandomValues === 'function' && typeof Uint8Array === 'function') {
      const callback = (c: any) => {
        const num = Number(c)
        return (num ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (num / 4)))).toString(
          16
        )
      }
      return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, callback)
    }
  }
  let timestamp = new Date().getTime()
  let performanceNow =
    (typeof performance !== 'undefined' && performance.now && performance.now() * 1000) || 0
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    let random = Math.random() * 16
    if (timestamp > 0) {
      random = ((timestamp + random) % 16) | 0
      timestamp = Math.floor(timestamp / 16)
    } else {
      random = ((performanceNow + random) % 16) | 0
      performanceNow = Math.floor(performanceNow / 16)
    }
    return (c === 'x' ? random : (random & 0x3) | 0x8).toString(16)
  })
}

/**
 * 解析 JSON 字符串
 *
 * @param str
 */
export function jsonParse(str: string) {
  try {
    return JSON.parse(str)
  } catch (e) {
    console.warn(`str[${str}] 不是一个 JSON 字符串`)
    return str
  }
}

/**
 * 截取字符串
 *
 * @param str 字符串
 * @param start 开始位置
 * @param end 结束位置
 */
export const subString = (str: string, start: number, end: number) => {
  if (str.length > end) {
    return str.slice(start, end)
  }
  return str
}

/**
 * 深度遍历对象，对其中的 JSON 字符串进行 parse
 * @param data 需要处理的数据（对象、数组或基本类型）
 * @returns 转换后的新数据
 */
export const deepJsonParse = <T>(data: T): T => {
  // 1. 处理 null 或非对象类型
  if (data === null || typeof data !== 'object') {
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data)
        if (typeof parsed === 'object' && parsed !== null) {
          return deepJsonParse(parsed) as T
        }
      } catch (_e) {
        // 解析失败，保留原字符串
      }
    }
    return data
  }

  // 2. 处理数组
  if (Array.isArray(data)) {
    return data.map((item) => deepJsonParse(item)) as T
  }

  // 3. 处理对象
  return Object.entries(data).reduce<Record<string, any>>((acc, [key, value]) => {
    acc[key] = deepJsonParse(value)
    return acc
  }, {}) as T
}

/**
 * 数组对象去重工具函数
 * 支持单字段/多字段联合去重，支持嵌套对象属性去重
 *
 * @param {Array} arr 需要去重的数组
 * @param {String|Array<String>} keys 去重依赖的字段名，支持点语法获取嵌套属性 (如 'id' 或 ['type', 'user.id'])
 * @param {Boolean} isDeepCopy 去重后的对象是否需要深拷贝，默认为 false (为 false 时新数组中的对象仍与原数组保持引用关系)
 * @returns {Array} 去重后的新数组
 */
export const uniqueArrayObjects = (arr: any[], keys: string | string[], isDeepCopy = false) => {
  // 1. 边界条件拦截：如果传入的不是有效数组，直接返回空数组
  if (!Array.isArray(arr) || arr.length === 0) {
    return []
  }

  // 2. 规范化 keys 参数：统一转换为数组形式，方便后续处理
  const keyList = Array.isArray(keys) ? keys : [keys]

  // 如果没有传入任何 key，则按照对象的 JSON 字符串进行整体去重
  if (keyList.length === 0) {
    const seen = new Set()
    return arr.filter((item) => {
      // 注意：对象属性顺序不同会导致 JSON.stringify 结果不同，此为简易全量匹配
      const str = JSON.stringify(item)
      if (seen.has(str)) return false
      seen.add(str)
      return true
    })
  }

  /**
   * 辅助函数：安全地根据点语法路径获取嵌套对象的属性值
   * 例如：getNestedValue({ user: { id: 1 } }, 'user.id') 返回 1
   *
   * @param {Object} obj 目标对象
   * @param {String} path 属性路径，如 'a.b.c'
   * @returns {Any} 属性对应的值，如果路径不存在则返回 undefined
   */
  const getNestedValue = (obj, path) => {
    if (obj === null || typeof obj !== 'object') return undefined
    // 将路径按 '.' 分割，依次向下取值
    return path.split('.').reduce((acc, part) => {
      // 如果当前层级已经是 null/undefined 或者不是对象，直接返回 undefined 防止报错
      if (acc === null || acc === undefined) return undefined
      return acc[part]
    }, obj)
  }

  // 3. 使用 Map 数据结构进行去重 (利用 Map 键名的唯一性)
  const map = new Map()

  arr.forEach((item) => {
    // 3.1 根据 keyList 提取当前对象的特征值
    const values = keyList.map((key) => {
      const val = getNestedValue(item, key)
      // 为了保证 undefined、函数、Symbol 等无法被 JSON 序列化的值也能作为唯一标识，
      // 同时避免对象引用导致的 Map 键名冲突，统一将其转为字符串
      if (val === undefined || typeof val === 'function' || typeof val === 'symbol') {
        return String(val) // 'undefined' 或 'function' 等
      }
      if (typeof val === 'object' && val !== null) {
        return JSON.stringify(val) // 嵌套对象/数组作为唯一标识时转为字符串
      }
      return val
    })

    // 3.2 将特征值数组转为字符串，作为 Map 的唯一键
    // 例如：keys=['id', 'type'], values=[1, 'A'] -> 联合键名 "1|A"
    const uniqueKey = values.join('|')

    // 3.3 如果 Map 中不存在该键名，则将对象存入 Map
    if (!map.has(uniqueKey)) {
      map.set(uniqueKey, item)
    }
  })

  // 4. 提取 Map 中的值组成新数组
  let result = Array.from(map.values())

  // 5. 深拷贝处理：如果调用者需要完全独立的数据副本，则进行深拷贝
  if (isDeepCopy) {
    result = JSON.parse(JSON.stringify(result))
  }

  return result
}
